import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Paper, Group, Text, Stack, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { getTravelTypeConfig } from "@/config";

import { Travel } from "@/utils/api/TravelService";

import { AppButton } from "@/components/generic/AppButton";
import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ActiveTravelBannerProps {
  /** The active travel to display */
  activeTravel: Travel | null;

  /** Callback when Add Waypoint button is clicked */
  onAddWaypoint?: () => void;

  /** Callback when Stop Trip button is clicked */
  onStopTrip?: () => void;

  /** Whether any action is loading */
  loading?: boolean;

  /** Variant of the banner */
  variant?: "default" | "compact";
}

// ============================================
// Helper Functions
// ============================================

/**
 * Calculate elapsed time from start to now
 */
const calculateElapsedTime = (startTime: string | undefined): string => {
  if (!startTime) return "--:--";

  const start = dayjs(startTime);
  const now = dayjs();
  const diffSeconds = now.diff(start, "second");

  const hours = Math.floor(diffSeconds / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

// ============================================
// Pulsing Dot Sub-Component
// ============================================

const PulsingDot: React.FC<{ size?: number }> = ({ size = 8 }) => (
  <Box
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      backgroundColor: "var(--mantine-color-green-6)",
      animation: "activeTravelPulse 2s ease-in-out infinite",
      flexShrink: 0,
    }}
  />
);

// ============================================
// Shared keyframes (injected once)
// ============================================

const PulseKeyframes: React.FC = () => (
  <style>
    {`
      @keyframes activeTravelPulse {
        0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.5); }
        70% { box-shadow: 0 0 0 5px rgba(22, 163, 74, 0); }
        100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
      }
    `}
  </style>
);

// ============================================
// Info Row Sub-Component
// ============================================

interface InfoRowProps {
  iconName: string;
  label: string;
  iconColor?: string;
  textColor?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({
  iconName,
  label,
  iconColor = "var(--mantine-color-neutral-5)",
  textColor = "neutral.6",
}) => (
  <Group gap={6} wrap="nowrap">
    <AppIcon iconName={iconName} size={15} color={iconColor} />
    <Text size="sm" c={textColor} lineClamp={1}>
      {label}
    </Text>
  </Group>
);

// ============================================
// Component
// ============================================

const ActiveTravelBannerComponent: React.FC<ActiveTravelBannerProps> = ({
  activeTravel,
  //onAddWaypoint,
  onStopTrip,
  loading = false,
  variant = "default",
}) => {
  const { t } = useTranslation(["travels", "common"]);

  // Elapsed time state (updates every second)
  const [elapsedTime, setElapsedTime] = useState<string>(() =>
    calculateElapsedTime(activeTravel?.firstDttm)
  );

  // Update elapsed time every second
  useEffect(() => {
    if (!activeTravel?.firstDttm) return;

    setElapsedTime(calculateElapsedTime(activeTravel.firstDttm));

    const interval = setInterval(() => {
      setElapsedTime(calculateElapsedTime(activeTravel.firstDttm));
    }, 1000);

    return () => clearInterval(interval);
  }, [activeTravel?.firstDttm]);

  // Get travel type config for icon/color
  const travelTypeConfig = useMemo(() => {
    return getTravelTypeConfig(activeTravel?.travelType);
  }, [activeTravel?.travelType]);

  // const handleAddWaypoint = useCallback(() => {
  //   onAddWaypoint?.();
  // }, [onAddWaypoint]);

  const handleStopTrip = useCallback(() => {
    onStopTrip?.();
  }, [onStopTrip]);

  // Don't render if no active travel
  if (!activeTravel) {
    return null;
  }

  // Get starting location from first record
  const startingLocation = activeTravel.firstRecord?.whereDone;

  // ── Compact variant (for app header integration) ──────────────
  if (variant === "compact") {
    return (
      <Paper
        p="xs"
        radius="sm"
        bg="white"
        style={{
          border: "1px solid var(--mantine-color-neutral-2)",
        }}
      >
        <Group gap="sm" justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <PulsingDot />
            <Text size="sm" fw={500} c="neutral.7" lineClamp={1}>
              {activeTravel.car?.label}
            </Text>
            <Text size="sm" c="neutral.6" ff="monospace">
              {elapsedTime}
            </Text>
          </Group>

          <Group gap={4} wrap="nowrap">
            <AppButton
              variant="secondary"
              size="xs"
              color="green"
              onClick={handleStopTrip}
              disabled={loading}
            >
              <AppIcon iconName="CircleCheck" size={16} />
            </AppButton>
          </Group>
        </Group>

        <PulseKeyframes />
      </Paper>
    );
  }

  // ── Default variant (informational display) ───────────────────
  return (
    <Stack gap="md">
      {/* Status Header */}
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <PulsingDot size={10} />
          <Text size="sm" fw={600} c="green.7">
            {t("travels:banner.tripInProgress")}
          </Text>
        </Group>

        {/* Elapsed Time */}
        <Paper
          px="sm"
          py={4}
          radius="sm"
          bg="neutral.0"
          style={{ border: "1px solid var(--mantine-color-neutral-2)" }}
        >
          <Group gap={6} wrap="nowrap">
            <AppIcon
              iconName="Clock"
              size={15}
              color="var(--mantine-color-neutral-5)"
            />
            <Text size="md" fw={700} c="neutral.8" ff="monospace">
              {elapsedTime}
            </Text>
          </Group>
        </Paper>
      </Group>

      {/* Trip Details */}
      <Paper
        p="md"
        radius="md"
        bg="neutral.0"
        style={{ border: "1px solid var(--mantine-color-neutral-2)" }}
      >
        <Stack gap="sm">
          {/* Vehicle */}
          <InfoRow
            iconName="Car"
            label={activeTravel.car?.label || t("travels:labels.unknownVehicle")}
            iconColor="var(--mantine-color-brand-6)"
            textColor="neutral.8"
          />

          {/* Travel Type */}
          {travelTypeConfig && (
            <InfoRow
              iconName={travelTypeConfig.icon}
              label={t(travelTypeConfig.translationKey)}
              iconColor={`var(--mantine-color-${travelTypeConfig.color}-6)`}
              textColor="neutral.6"
            />
          )}

          {/* Starting Location */}
          {startingLocation && (
            <InfoRow
              iconName="MapPinFilled"
              label={startingLocation}
              iconColor="var(--mantine-color-neutral-5)"
              textColor="neutral.6"
            />
          )}

          {/* Purpose */}
          {activeTravel.purpose && (
            <InfoRow
              iconName="FileText"
              label={activeTravel.purpose}
              iconColor="var(--mantine-color-neutral-4)"
              textColor="neutral.5"
            />
          )}
        </Stack>
      </Paper>

      <PulseKeyframes />
    </Stack>
  );
};

export const ActiveTravelBanner = React.memo(ActiveTravelBannerComponent);